/**
 * InternalNotesPanel - Encart de notes equipe en sticky droit.
 *
 * Notes INTERNES, NON visibles dans le PDF dossier de presentation.
 * Reutilise l'endpoint /api/pdf-notes/{entity_type}/{entity_id} (deja existant).
 *
 * Props :
 *  - entityType ('analysis' | 'financing' | 'project'...)
 *  - entityId
 *  - title (optionnel)
 *  - collapsible (defaut true)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTeamHandles } from '../hooks/useTeamHandles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "a l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return iso; }
};

const InternalNotesPanel = ({
  entityType,
  entityId,
  title = 'Notes internes (off)',
  collapsible = true,
}) => {
  const teamHandles = useTeamHandles();
  const [open, setOpen] = useState(true);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [me, setMe] = useState({});
  const [showMenu, setShowMenu] = useState(false);
  const [filter, setFilter] = useState('');
  const taRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    if (!entityType || !entityId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pdf-notes/${entityType}/${entityId}`);
      setNotes(r.data?.notes || []);
    } catch { /* silencieux */ } finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchNotes();
    axios.get(`${API}/auth/me`).then(r => setMe(r.data || {})).catch(() => {});
  }, [fetchNotes]);

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    const cursor = e.target.selectionStart;
    const before = v.slice(0, cursor);
    const m = before.match(/@([a-zA-Z]*)$/);
    if (m) { setShowMenu(true); setFilter(m[1].toLowerCase()); }
    else setShowMenu(false);
  };

  const pickMention = (handle) => {
    if (!taRef.current) return;
    const ta = taRef.current;
    const cursor = ta.selectionStart;
    const before = text.slice(0, cursor).replace(/@[a-zA-Z]*$/, `@${handle} `);
    const after = text.slice(cursor);
    setText(before + after);
    setShowMenu(false);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(before.length, before.length); }, 10);
  };

  const filtered = teamHandles.filter(h => h.handle.startsWith(filter));

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/pdf-notes/${entityType}/${entityId}`, { text: t });
      setNotes([r.data, ...notes]);
      setText('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur ajout note');
    } finally { setPosting(false); }
  };

  const remove = async (note_id) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await axios.delete(`${API}/pdf-notes/${note_id}`);
      setNotes(notes.filter(n => n.note_id !== note_id));
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="realease-card overflow-hidden" data-testid="internal-notes-panel">
      <button
        type="button"
        onClick={() => collapsible && setOpen(!open)}
        className={`w-full px-4 py-3 flex items-center justify-between bg-[#FDF8F0] border-b border-[#E8DCC4] ${collapsible ? 'cursor-pointer hover:bg-[#F5EDD8]' : 'cursor-default'}`}
        data-testid="internal-notes-toggle">
        <span className="text-sm font-bold text-[#A57F4F] flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          {title}
          {notes.length > 0 && (
            <span className="text-[10px] bg-[#A57F4F] text-white px-1.5 py-0.5 rounded-full font-bold">{notes.length}</span>
          )}
        </span>
        {collapsible && (
          <svg className={`w-4 h-4 text-[#A57F4F] transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {open && (
        <div className="p-3">
          <p className="text-[10px] text-gray-500 mb-2 italic">
            Notes equipe, non visibles dans le PDF dossier. Tapez @ pour mentionner.
          </p>

          <div className="relative">
            <textarea
              ref={taRef}
              value={text}
              onChange={handleChange}
              placeholder="Ecrire une note interne..."
              className="w-full border border-gray-200 rounded-lg p-2 text-xs h-20 resize-none focus:border-[#A57F4F] focus:outline-none"
              data-testid="internal-note-input" />
            {showMenu && filtered.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-44 overflow-auto" data-testid="internal-note-mention-menu">
                {filtered.map(h => (
                  <button key={h.handle} type="button" onClick={() => pickMention(h.handle)}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#A57F4F]/10 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: h.color || '#13736D' }}>
                      {h.handle.charAt(0).toUpperCase()}
                    </span>
                    <span><b>@{h.handle}</b> <span className="text-gray-400">{h.name}</span></span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={add}
            disabled={posting || !text.trim()}
            className="mt-2 w-full px-3 py-1.5 bg-[#A57F4F] text-white text-xs font-semibold rounded hover:bg-[#8C6A3F] disabled:opacity-50"
            data-testid="internal-note-add">
            {posting ? 'Ajout...' : '+ Ajouter une note interne'}
          </button>

          <div className="mt-3 space-y-2 max-h-[40vh] overflow-y-auto pr-1">
            {loading && <p className="text-xs text-gray-400 italic">Chargement...</p>}
            {!loading && notes.length === 0 && (
              <p className="text-[11px] text-gray-400 italic text-center py-2">Aucune note pour le moment.</p>
            )}
            {notes.map(n => {
              const canDelete = n.author_id === me.user_id || ['admin', 'ceo'].includes(me.role);
              return (
                <div key={n.note_id} className="p-2 bg-[#FDF8F0] border border-[#E8DCC4] rounded text-xs" data-testid={`internal-note-${n.note_id}`}>
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <div>
                      <p className="font-semibold text-gray-800 text-[11px]">{n.author_name}</p>
                      <p className="text-gray-400 text-[10px]">{formatRelative(n.created_at)}</p>
                    </div>
                    {canDelete && (
                      <button onClick={() => remove(n.note_id)} className="text-red-400 hover:text-red-600 text-[10px]">
                        Suppr.
                      </button>
                    )}
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap leading-snug">{n.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalNotesPanel;
